import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { API_URL, FORMES_JURIDIQUES } from './constants';

const EMPTY_FORM = {
  raison_sociale: '',
  forme_juridique: 'auto_entrepreneur',
  siret: '',
  siren: '',
  code_naf: '',
  adresse: '',
  code_postal: '',
  ville: '',
  pays: 'France',
  contact_nom: '',
  contact_prenom: '',
  contact_email: '',
  contact_telephone: '',
  domaine_activite: '',
  date_debut_relation: '',
  montant_annuel_estime: '',
  emploie_salaries: false,
  salaries_etrangers: false,
  secteur_btp: false,
  statut: 'actif',
  notes: '',
  tags: ''
};

const toForm = (st) => {
  if (!st) return { ...EMPTY_FORM };
  const form = { ...EMPTY_FORM };
  Object.keys(EMPTY_FORM).forEach((key) => {
    if (st[key] !== undefined && st[key] !== null) form[key] = st[key];
  });
  form.tags = Array.isArray(st.tags) ? st.tags.join(', ') : (st.tags || '');
  form.montant_annuel_estime = st.montant_annuel_estime ?? '';
  return form;
};

/**
 * Dialogue de création / modification d'un sous-traitant, avec pré-remplissage par SIRET
 * (API publique recherche-entreprises.api.gouv.fr via le backend).
 */
const SousTraitantFormDialog = ({ open, onOpenChange, sousTraitant, onSaved }) => {
  const [form, setForm] = useState(toForm(sousTraitant));
  const [saving, setSaving] = useState(false);
  const [looking, setLooking] = useState(false);
  const [lookupInfo, setLookupInfo] = useState(null);
  const isEdit = !!sousTraitant?.id;

  useEffect(() => {
    if (open) {
      setForm(toForm(sousTraitant));
      setLookupInfo(null);
    }
  }, [open, sousTraitant]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleLookup = async () => {
    const digits = (form.siret || '').replace(/\D/g, '');
    if (digits.length !== 14 && digits.length !== 9) {
      toast.error('Saisissez un SIRET (14 chiffres) ou un SIREN (9 chiffres)');
      return;
    }
    setLooking(true);
    try {
      const res = await axios.get(`${API_URL}/sous-traitants/lookup-siret/${digits}`);
      const d = res.data;
      setForm((f) => ({
        ...f,
        raison_sociale: d.raison_sociale || f.raison_sociale,
        siret: d.siret || f.siret,
        siren: d.siren || f.siren,
        code_naf: d.code_naf || f.code_naf,
        adresse: d.adresse || f.adresse,
        code_postal: d.code_postal || f.code_postal,
        ville: d.ville || f.ville,
        forme_juridique: d.forme_juridique_guess && FORMES_JURIDIQUES[d.forme_juridique_guess] ? d.forme_juridique_guess : f.forme_juridique
      }));
      setLookupInfo(d);
      toast.success('Informations récupérées, vérifiez-les avant d\'enregistrer');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Recherche impossible, renseignez les informations manuellement'));
    } finally {
      setLooking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.raison_sociale.trim()) {
      toast.error('La raison sociale est obligatoire');
      return;
    }
    const payload = {
      ...form,
      raison_sociale: form.raison_sociale.trim(),
      siret: form.siret ? form.siret.replace(/\D/g, '') : null,
      siren: form.siren ? form.siren.replace(/\D/g, '') : null,
      montant_annuel_estime: form.montant_annuel_estime === '' ? null : Number(form.montant_annuel_estime),
      date_debut_relation: form.date_debut_relation || null,
      tags: String(form.tags || '').split(',').map((t) => t.trim()).filter(Boolean)
    };
    ['code_naf', 'adresse', 'code_postal', 'ville', 'contact_nom', 'contact_prenom', 'contact_email',
      'contact_telephone', 'domaine_activite', 'notes'].forEach((k) => { if (payload[k] === '') payload[k] = null; });
    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await axios.put(`${API_URL}/sous-traitants/${sousTraitant.id}`, payload);
        toast.success('Sous-traitant mis à jour');
      } else {
        res = await axios.post(`${API_URL}/sous-traitants`, payload);
        toast.success('Sous-traitant créé');
      }
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Enregistrement impossible'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier le sous-traitant' : 'Nouveau sous-traitant'}</DialogTitle>
          <DialogDescription>
            Identité, contact et indicateurs qui déterminent les documents exigibles au titre de l'obligation de vigilance.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Identification</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2 space-y-1">
                <Label htmlFor="st-siret">SIRET</Label>
                <div className="flex gap-2">
                  <Input id="st-siret" value={form.siret} onChange={(e) => set('siret', e.target.value)} placeholder="732 829 320 00074" />
                  <Button type="button" variant="outline" onClick={handleLookup} disabled={looking} className="shrink-0 gap-2">
                    {looking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    Rechercher par SIRET
                  </Button>
                </div>
                {lookupInfo?.etat_administratif && lookupInfo.etat_administratif !== 'A' && (
                  <p className="text-xs text-red-600">Attention : établissement signalé comme fermé dans le répertoire SIRENE.</p>
                )}
                {lookupInfo?.date_creation && (
                  <p className="text-xs text-slate-500">Entreprise créée le {new Date(lookupInfo.date_creation).toLocaleDateString('fr-FR')}{lookupInfo.dirigeant ? ` — dirigeant : ${lookupInfo.dirigeant}` : ''}</p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="st-siren">SIREN</Label>
                <Input id="st-siren" value={form.siren} onChange={(e) => set('siren', e.target.value)} placeholder="732829320" />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <Label htmlFor="st-rs">Raison sociale *</Label>
                <Input id="st-rs" value={form.raison_sociale} onChange={(e) => set('raison_sociale', e.target.value)} required />
              </div>
              <div className="space-y-1">
                <Label>Forme juridique</Label>
                <Select value={form.forme_juridique} onValueChange={(v) => set('forme_juridique', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(FORMES_JURIDIQUES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="st-naf">Code NAF</Label>
                <Input id="st-naf" value={form.code_naf} onChange={(e) => set('code_naf', e.target.value)} placeholder="43.21A" />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <Label htmlFor="st-domaine">Domaine d'intervention</Label>
                <Input id="st-domaine" value={form.domaine_activite} onChange={(e) => set('domaine_activite', e.target.value)} placeholder="Électricité, nettoyage, transport…" />
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Adresse</h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="sm:col-span-4 space-y-1">
                <Label htmlFor="st-adresse">Adresse</Label>
                <Input id="st-adresse" value={form.adresse} onChange={(e) => set('adresse', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="st-cp">Code postal</Label>
                <Input id="st-cp" value={form.code_postal} onChange={(e) => set('code_postal', e.target.value)} />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <Label htmlFor="st-ville">Ville</Label>
                <Input id="st-ville" value={form.ville} onChange={(e) => set('ville', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="st-pays">Pays</Label>
                <Input id="st-pays" value={form.pays} onChange={(e) => set('pays', e.target.value)} />
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Contact</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="st-cprenom">Prénom</Label>
                <Input id="st-cprenom" value={form.contact_prenom} onChange={(e) => set('contact_prenom', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="st-cnom">Nom</Label>
                <Input id="st-cnom" value={form.contact_nom} onChange={(e) => set('contact_nom', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="st-cemail">Email (relances)</Label>
                <Input id="st-cemail" type="email" value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="st-ctel">Téléphone</Label>
                <Input id="st-ctel" value={form.contact_telephone} onChange={(e) => set('contact_telephone', e.target.value)} />
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Relation commerciale</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="st-debut">Début de la relation</Label>
                <Input id="st-debut" type="date" value={form.date_debut_relation} onChange={(e) => set('date_debut_relation', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="st-montant">Montant annuel estimé (€ HT)</Label>
                <Input id="st-montant" type="number" min="0" step="100" value={form.montant_annuel_estime} onChange={(e) => set('montant_annuel_estime', e.target.value)} />
                <p className="text-xs text-slate-500">Obligation de vigilance dès 5 000 € HT cumulés par an.</p>
              </div>
              <div className="space-y-1">
                <Label>Statut</Label>
                <Select value={form.statut} onValueChange={(v) => set('statut', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="actif">Actif</SelectItem>
                    <SelectItem value="suspendu">Suspendu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
              <label className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm dark:border-slate-700">
                <span>Emploie des salariés</span>
                <Switch checked={!!form.emploie_salaries} onCheckedChange={(v) => set('emploie_salaries', v)} />
              </label>
              <label className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm dark:border-slate-700">
                <span>Salariés étrangers</span>
                <Switch checked={!!form.salaries_etrangers} onCheckedChange={(v) => set('salaries_etrangers', v)} />
              </label>
              <label className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm dark:border-slate-700">
                <span>Secteur BTP (décennale)</span>
                <Switch checked={!!form.secteur_btp} onCheckedChange={(v) => set('secteur_btp', v)} />
              </label>
            </div>
          </section>

          <section className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="st-tags">Tags (séparés par des virgules)</Label>
                <Input id="st-tags" value={form.tags} onChange={(e) => set('tags', e.target.value)} placeholder="chantier A, urgent" />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="st-notes">Notes internes</Label>
              <Textarea id="st-notes" rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </div>
          </section>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-[#0F5156] hover:bg-[#0F5156]/90">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEdit ? 'Enregistrer' : 'Créer le sous-traitant'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SousTraitantFormDialog;
